import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { faChartLine, faMoneyBill, faPercent, faUser, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button, Link, Stack, Typography } from '@mui/material';
import cn from 'classnames';
import DOMPurify from 'dompurify';
import { useUnit } from 'effector-react';

import { ApprovedDialog } from './ApprovedDialog';
import { ExecutingWarningDialog } from './ExecutingWarningDialog';
import { useCancelMarketLimitSignal, useCheckSignal, usePortfolio } from './queries';
import { SignalDialog } from './SignalDialog';
import { ChartLegend } from '../../../components/Chart/ChartLegend';
import { PieChart } from '../../../components/Chart/PieChart';
import { Navigation } from '../../../components/Navigation';
import { ProtectedComponent } from '../../../components/ProtectedComponent';
import { Timer } from '../../../components/Timer';
import { Strategies$ } from '../../../effector/strategies';
import { StrategyStatus } from '../../../types/strategy';
import { isSuccessResponse, parseApiError } from '../../../utils/apiErrors';
import { UserAction } from '../../../utils/permissions';
import { PAGE_STRATEGIES } from '../../Routes';

type Props = {
  id: string;
  isChild: boolean;
  parentStrategy: string;
};

const normalizeWeight = (weight: number | string) => {
  const parsedWeight = typeof weight === 'number' ? weight : Number(weight);
  return Number.isFinite(parsedWeight) ? parsedWeight : 0;
};

export const Portfolio: React.FC<Props> = ({ id, isChild, parentStrategy }) => {
  const [activePositionIndex, setActivePositionIndex] = useState<number | null>(null);
  const [openSignalDialog, setOpenSignalDialog] = useState(false);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openExecutingWarningDialog, setOpenExecutingWarningDialog] = useState(false);
  const [error, setError] = useState('');

  const strategies = useUnit(Strategies$);

  const strategy = useMemo(() => strategies.find((item) => item.id === id), [id, strategies]);

  const { data: portfolio, refetch: refetchPortfolio } = usePortfolio(id, strategy?.status);
  const { data: signalDelay, refetch: checkSignal } = useCheckSignal(id);

  const handleError = useCallback((result: unknown) => {
    setError(parseApiError(result));
  }, []);

  const handleSuccess = useCallback(
    (response: unknown) => {
      if (!isSuccessResponse(response)) {
        setError(parseApiError(response));
        return;
      }

      checkSignal();
      refetchPortfolio();
    },
    [checkSignal, refetchPortfolio],
  );

  const { mutate: cancelMarketSignal } = useCancelMarketLimitSignal({
    onError: handleError,
    onSuccess: handleSuccess,
  });

  const endTimerDate = useMemo(() => {
    if (signalDelay?.result.secondsLeft) {
      const t = new Date();
      t.setSeconds(t.getSeconds() + signalDelay.result.secondsLeft);
      return t;
    }
  }, [signalDelay]);

  const executingNumber = signalDelay?.result.executingNumber ?? 0;

  const openModal = useCallback(() => {
    if (executingNumber > 0) {
      setOpenExecutingWarningDialog(true);
    } else {
      setOpenSignalDialog(true);
    }
  }, [executingNumber]);

  const closeModal = useCallback(() => {
    setOpenSignalDialog(false);
  }, []);

  const handleExecutingWarningCancel = useCallback(() => {
    setOpenExecutingWarningDialog(false);
  }, []);

  const handleExecutingWarningConfirm = useCallback(() => {
    setOpenExecutingWarningDialog(false);
    setOpenSignalDialog(true);
  }, []);

  const openApproveModal = useCallback(() => {
    setOpenApproveDialog(true);
    checkSignal();
  }, [checkSignal]);

  const closeApproveModal = useCallback(() => {
    setOpenApproveDialog(false);
  }, []);

  const handleCancelLimitOrder = useCallback(() => {
    setError('');
    cancelMarketSignal({
      strategyId: strategy?.id as string,
      managerId: strategy?.managerId as number,
    });
  }, [strategy, cancelMarketSignal]);

  const params = useMemo(() => {
    if (!portfolio?.result) {
      return [];
    }

    return [
      {
        icon: <FontAwesomeIcon icon={faMoneyBill} className="footer__icon" />,
        name: 'Свободные средства',
        value: portfolio.result.currentFree,
      },
      {
        icon: <FontAwesomeIcon icon={faChartLine} className="footer__icon" />,
        name: 'Realized P&L',
        value: portfolio.result.realizedPL,
        className: portfolio.result.realizedPL >= 0 ? 'rise' : 'fall',
      },
      {
        icon: <FontAwesomeIcon icon={faChartLine} className="footer__icon" />,
        name: 'Unrealized P&L',
        value: portfolio.result.unrealizedPL,
        className: portfolio.result.unrealizedPL >= 0 ? 'rise' : 'fall',
      },
      {
        icon: <FontAwesomeIcon icon={faPercent} className="footer__icon" />,
        name: 'Загрузка',
        value: portfolio.result.portfolioWeight,
      },
      {
        icon: <FontAwesomeIcon icon={faExclamationTriangle} className="footer__icon" />,
        name: 'НПР1',
        value: portfolio.result.npr1,
      },
    ];
  }, [portfolio?.result]);

  const progress = useMemo(() => {
    if (!portfolio?.result) {
      return 0;
    }
    return portfolio?.result.portfolioWeight > 100 ? 100 : portfolio?.result.portfolioWeight;
  }, [portfolio?.result]);

  const chartPositions = useMemo(() => {
    return (portfolio?.result?.positions ?? []).map((position) => ({
      ...position,
      weight: normalizeWeight(position.weight),
    }));
  }, [portfolio?.result?.positions]);

  useEffect(() => {
    setActivePositionIndex((prev) => {
      if (prev === null) {
        return prev;
      }

      return prev < chartPositions.length ? prev : null;
    });
  }, [chartPositions]);

  if (!portfolio?.result) {
    return null;
  }

  const isLimit = signalDelay?.result.isLimit || false;

  return (
    <div className="strategy-portfolio">
      <Navigation items={[{ name: 'Портфель', path: `${PAGE_STRATEGIES}/${id}` }]} />
      <table className="strategy-portfolio__params">
        <tbody>
          <tr>
            <td className="strategy-portfolio__params-item strategy-portfolio__params-size">
              <FontAwesomeIcon icon={faUser} className="footer__icon" />
            </td>
            <td className="strategy-portfolio__params-item">Клиентов</td>
            <td className={'strategy-portfolio__params-item strategy-portfolio__params-item-right'}>
              {portfolio?.result?.clients}
            </td>
          </tr>
          <tr>
            <td className="strategy-portfolio__params-item strategy-portfolio__params-size">
              <FontAwesomeIcon icon={faMoneyBill} className="footer__icon" />
            </td>
            <td className="strategy-portfolio__params-item">Активов</td>
            <td className={'strategy-portfolio__params-item strategy-portfolio__params-item-right'}>
              {portfolio?.result?.totalAssets}
            </td>
          </tr>
        </tbody>
      </table>
      <div className="strategy-portfolio__currency">
        Валюта стратегии: <span className="strategy-portfolio__currency-value">{portfolio.result.currency}</span>
      </div>
      <table className="strategy-portfolio__params">
        <tbody>
          {params.map(({ icon, name, value, className }, index) => (
            <tr key={index}>
              <td className="strategy-portfolio__params-item strategy-portfolio__params-size">{icon}</td>
              <td
                className="strategy-portfolio__params-item"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(name) }}
              ></td>
              <td className={cn('strategy-portfolio__params-item strategy-portfolio__params-item-right', className)}>
                {value}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="strategy-portfolio__progress-bar-wrapper">
        <div className="strategy-portfolio__progress-bar" style={{ width: `${progress}%` }} />
      </div>

      <div className="strategy-portfolio__signal">
        <ProtectedComponent userAction={UserAction.strategyAdd}>
          {isChild && (
            <span className="error-message">
              Данная стратегия является дочерней и дублирует сделки стратегии «{parentStrategy}». Самостоятельная подача
              сигнала по данной стратегии невозможна
            </span>
          )}
          {!isChild && (strategy?.status === StrategyStatus.Standard || strategy?.status === StrategyStatus.Closed) && (
            <Stack spacing={1}>
              <Button
                type="button"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2 }}
                disabled={!!endTimerDate || strategy?.isFrozen}
                onClick={openModal}
              >
                Новый сигнал
              </Button>

              {executingNumber > 0 && (
                <Typography variant="caption" color="warning.main" align="center" sx={{ display: 'block' }}>
                  Клиентов в исполнении: <strong>{executingNumber}</strong>
                </Typography>
              )}
              {endTimerDate && (
                <div className="strategy-portfolio__timer">
                  До возможности подачи нового сигнала осталось{' '}
                  <Timer endDate={endTimerDate} timeFinishCallback={checkSignal} />
                </div>
              )}
              {isLimit ? (
                <Link component="button" onClick={handleCancelLimitOrder} underline="always" sx={{ cursor: 'pointer' }}>
                  Отменить лимитную заявку
                </Link>
              ) : null}
              {strategy?.isFrozen && <div className="strategy-portfolio__timer">Стратегия заморожена</div>}
              {error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {error}
                </Alert>
              )}
            </Stack>
          )}
        </ProtectedComponent>
      </div>

      <div className="strategy-portfolio__chart">
        <PieChart data={chartPositions} activeIndex={activePositionIndex} onSliceHover={setActivePositionIndex} />
      </div>
      <ChartLegend positions={chartPositions} activeIndex={activePositionIndex} onItemHover={setActivePositionIndex} />

      {openExecutingWarningDialog && (
        <ExecutingWarningDialog
          executingNumber={executingNumber}
          onCancel={handleExecutingWarningCancel}
          onConfirm={handleExecutingWarningConfirm}
        />
      )}
      {openSignalDialog && <SignalDialog strategyId={id as string} onClose={closeModal} onApprove={openApproveModal} />}
      {openApproveDialog && <ApprovedDialog onClose={closeApproveModal} />}
    </div>
  );
};
